import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, ListChecks, Tags, Sparkles, HeartPulse, Layers, Package, Settings, LogOut, ArrowLeft, MessageCircle, Users, Wand2, Bot, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { Monogram } from "@/components/brand/Logo";

const NAV = [
  ["", "Analisi funnel", BarChart3], ["chat", "Chat live", MessageCircle], ["consulente", "Consulente AI", Bot], ["clienti", "Clienti", Users], ["domande", "Domande", ListChecks], ["tag", "Tag", Tags],
  ["profili", "Skin Profiles", Sparkles], ["esigenze", "Beauty Needs", HeartPulse], ["routine", "Routine Builder", Layers],
  ["prodotti", "Prodotti", Package], ["regole-tag", "Regole tag", Wand2], ["regole-routine", "Regole routine", SlidersHorizontal], ["impostazioni", "Impostazioni", Settings],
];

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 880; g.gain.value = 0.08;
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.18);
  } catch { /* audio not available */ }
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [unread, setUnread] = useState(0);
  const prevUnread = useRef(null);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); setStatus(data.is_admin ? "ok" : "forbidden"); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (status !== "ok") return;
    const poll = () => api.get("/admin/chat/unread").then(({ data }) => {
      if (prevUnread.current !== null && data.unread > prevUnread.current) {
        beep();
        toast("Nuovo messaggio in chat", { description: "Un cliente ti ha scritto.", action: { label: "Apri", onClick: () => navigate("/admin/chat") } });
      }
      prevUnread.current = data.unread;
      setUnread(data.unread);
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [status, navigate]);

  const logout = async () => { await api.post("/auth/logout").catch(() => {}); navigate("/admin/login", { replace: true }); };

  if (status === "loading") return <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-loading" />;
  if (status === "forbidden") return (
    <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center px-6" data-testid="admin-forbidden">
      <div className="text-center max-w-sm">
        <h1 className="font-serif text-3xl">Accesso non autorizzato</h1>
        <p className="text-sm text-[#8C8275] mt-3">{user?.email} non è tra gli amministratori. Chiedi a un admin di aggiungerti nelle Impostazioni.</p>
        <button data-testid="admin-forbidden-logout-btn" className="fs-btn mt-8" onClick={logout}>Esci</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F3EF] lg:flex" data-testid="admin-layout">
      <aside className="lg:w-64 bg-[#1A1A18] text-[#FAF8F5] lg:min-h-screen lg:sticky lg:top-0 lg:h-screen flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-3 px-6 py-6 border-b border-white/10">
          <Monogram size={40} className="rounded-full bg-[#FAF8F5] p-1" />
          <div><p className="font-serif text-lg leading-none">FineSkin</p><p className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">Advisor Admin</p></div>
        </div>
        <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible px-3 py-3 gap-1 flex-1">
          <Link to="/" data-testid="admin-to-customer-app-btn"
            className="flex items-center gap-2 px-3 py-2.5 rounded text-sm whitespace-nowrap border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#1A1A18] transition-colors lg:mb-2">
            <ArrowLeft size={16} strokeWidth={1.5} /> Web app cliente
          </Link>
          {NAV.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/admin${path ? `/${path}` : ""}`} end data-testid={`admin-nav-${path || "analytics"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded text-sm whitespace-nowrap transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}>
              <Icon size={16} strokeWidth={1.5} /> {label}
              {path === "chat" && unread > 0 && (
                <span data-testid="admin-chat-unread-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#C5A059] text-[#1A1A18] text-[11px] font-medium flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
          <button data-testid="admin-logout-mobile-btn" onClick={logout} className="lg:hidden flex items-center gap-2 px-3 py-2.5 rounded text-sm whitespace-nowrap text-white/60 hover:text-white"><LogOut size={16} strokeWidth={1.5} /> Esci</button>
        </nav>
        <div className="hidden lg:block px-6 py-5 border-t border-white/10 text-xs text-white/60 space-y-3">
          <p className="truncate" data-testid="admin-user-email">{user?.email}</p>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 hover:text-white"><LogOut size={14} /> Esci</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-5 sm:p-8 lg:p-10">
        <Outlet />
      </main>
    </div>
  );
}
